<template>
  <div class="address-box">
    <div class="flex">
      <div class="flex items-center justify-end" style="width: 25%">
        <el-popover
          v-if="eSignsArr?.length > 0 || isMaskedSignature"
          :disabled="!isMaskedSignature"
          placement="bottom"
          :width="180"
          trigger="click"
        >
          <template #reference>
            <el-button type="primary" class="edit-button" style="position: relative" @click="onDisplaySignature">
              <IconSignature />
              <IconMaskedLock v-if="isMaskedSignature" class="absolute" style="bottom: 5px; right: 0" />
            </el-button>
          </template>
          <div style="background-color: white; color: #ff0000">{{ $t('Require View Permission') }}</div>
        </el-popover>
        <el-popover
          v-if="Object.keys(imageUrlsArr).length > 0 || isMaskedImage"
          :disabled="!isMaskedImage"
          placement="bottom"
          :width="180"
          trigger="click"
        >
          <template #reference>
            <el-button type="primary" class="edit-button" style="position: relative" @click="onDisplayActivateImage">
              <IconImage />
              <IconMaskedLock v-if="isMaskedImage" class="absolute" style="bottom: 5px; right: 0" />
            </el-button>
          </template>
          <div style="background-color: white; color: #ff0000">{{ $t('Require View Permission') }}</div>
        </el-popover>
      </div>
    </div>

    <ManufacturingDataBlocks @show-adjustment="onShowAdjustment" @show-list-string="showListStringModal" />

    <div v-if="isFilesExist">
      <div v-for="(value, key) in fileList" :key="key" class="file-and-value">
        <div class="text-right">{{ value?.label }} :</div>
        <a>
          <div class="text-left" v-for="(el, idx) in value?.files" :key="`${key}_${idx}`">
            <div @click="onPreview(el.url)">
              <IconListFile />
              <div class="w-full py-2.5">
                <span>{{ el?.name }}</span>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
  <ImageViewerDialog v-model:imageDialogVisible="imageDialogVisible" :image="imageObj" />
  <AdjustmentDataDialog v-model:isShow="isShowAdjustmentField" :adjustmentField="adjustmentField" />

  <!-- List String Modal -->
  <el-dialog
    v-model="listStringModalVisible"
    :title="currentFieldLabel"
    width="100%"
    center
    custom-class="list-string-modal"
    :show-close="false"
  >
    <div>
      <div style="margin-bottom: 16px; font-weight: bold;">
        {{ $t('total') }}: {{ listStringData.length }} {{ listStringData.length === 1 ? $t('item') : $t('items') }}
      </div>
      <div style="max-height: 400px; overflow-y: auto;">
        <table style="width: 100%; border-collapse: collapse;">
          <tbody>
            <tr v-for="(item, index) in listStringData" :key="index">
              <td style="border: 1px solid #ddd; padding: 8px;">#{{ index + 1 }}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">{{ item.value }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <template #footer>
      <el-button @click="closeListStringModal" type="primary">
        {{ $t('ok') }}
        <KeyenceHTHardKeyButton
          v-if="isKeyenceEnterpriseBrowser"
          :hardkey="KeyenceHardKey.ENTER"
          :function="closeListStringModal"
          :visible="listStringModalVisible"
          :newLayer="true"
        />
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    center
    top="0"
    :model-value="signatureDialogVisible"
    :title="eSignCarouselHeaderLabel"
    @closed="signatureDialogVisible = false"
  >
    <div>
      <el-carousel @change="onESignCarouselChange" arrow="always" :autoplay="false" indicator-position="none">
        <el-carousel-item v-for="(eSign, key) in eSignsArr" :key="key">
          <div style="border: 1px solid #c4c4c4; place-self: center">
            <img :src="eSign.eSign" alt="eSign" />
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button id="AppDialog_closeDialog" type="primary" @click="signatureDialogVisible = false">
          {{ $t('ok') }}
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="() => (signatureDialogVisible = false)"
            :visible="signatureDialogVisible"
            :newLayer="true"
          />
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import AdjustmentDataDialog from '@/components/dialog/AdjustmentDataDialog.vue'
import ImageViewerDialog from '@/components/ImageViewerDialog.vue'
import ManufacturingDataBlocks from '@/components/ManufacturingDataBlocks.vue'
import IconImage from '@/components/svg/IconImage.vue'
import IconMaskedLock from '@/components/svg/IconMaskedLock.vue'
import IconSignature from '@/components/svg/IconSignature.vue'
import { ICustomFieldShowData } from '@/store/type'
import { MASKED_ANNOTATION } from '@/utils/constants'
import { Linkify } from '@/utils/directives'
import { arrayToObject, objectToArray, toSnakeCase } from '@/utils/helpers'
import { isEmpty } from '@/utils/typeChecker'
import { IDynamicCustomFieldData, ILabelDisplay, IListStringItem } from '@/utils/types/commonTypes'
import { ICustomFieldData, IFile } from '@/utils/types/fieldData'
import cloneDeep from 'lodash/cloneDeep'
import { Options, Vue } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import IconListFile from './svg/IconListFile.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'

export interface IFileInfo {
  files: {
    url: string
    name: string
  }[]
  label: string
}

@Options({
  directives: {
    linkify: Linkify,
  },
  components: {
    IconImage,
    IconMaskedLock,
    IconSignature,
    IconListFile,
    ImageViewerDialog,
    AdjustmentDataDialog,
    ManufacturingDataBlocks,
    KeyenceHTHardKeyButton,
  },
  name: 'DetailAddressBox',
  methods: { toSnakeCase },
  emits: ['update:onRefreshReferenceFieldClick'],
})
export default class DetailAddressBox extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  @Prop({ type: Object }) readonly eSigns!: ICustomFieldData[]
  @Prop({ type: Boolean }) readonly isMaskedImage!: boolean
  @Prop({ type: Boolean }) readonly isMaskedSignature!: boolean
  @Prop({ type: String }) readonly view?: string
  @Prop({ type: Object }) readonly data!: Record<string, ILabelDisplay>

  @Prop({ type: Object }) readonly fileLinks!: Record<string, ICustomFieldData>
  @Prop({ type: Object }) readonly fileImages!: Record<string, IDynamicCustomFieldData>
  @Prop({ type: Boolean }) readonly editable?: boolean
  localeList = Object.freeze(['ja', 'en', 'en-us', 'zh-tw', 'zh-cn'])

  imageDialogVisible = false
  signatureDialogVisible = false
  eSignCarouselIndex = 0
  isShowAdjustmentField = false
  adjustmentField = {} as ICustomFieldShowData
  listStringModalVisible = false
  listStringData: IListStringItem[] = []
  currentFieldLabel = ''

  get imageObj() {
    if (this.fileImages) {
      // FilterData use later
      const filterData = Object.values(this.fileImages).reduce((acc, curr) => {
        if (curr.files) {
          curr.files = curr.files.map((val) => {
            return { ...val, label: curr.label }
          })
          return { ...acc, [curr.label || '']: curr.files }
        }
        return acc
      }, {})
      return filterData || {}
    }
    return {}
  }

  get eSignCarouselHeaderLabel() {
    return this.eSignsArr[this.eSignCarouselIndex]?.label || ''
  }

  get eSignsArr() {
    return this.eSigns?.filter((e) => !!e?.eSign) || []
  }

  get imageUrlsArr() {
    if (this.fileImages) {
      let data: IFile[] = []
      // FilterData use later
      Object.values(this.fileImages).map((curr) => {
        if (curr.files) {
          data = [...data, ...curr?.files]
        }
      }, {})
      return data
    }

    return []
  }

  get dataList() {
    if (this.data) {
      const data = objectToArray(cloneDeep(this.data))
      return arrayToObject(
        data.filter((data) => {
          return !isEmpty(data.value.value)
        })
      )
    }
  }

  get fileList(): { [key: string]: IFileInfo } {
    const fileLinks = this.fileLinks || {}
    const files = Object.keys(fileLinks).reduce((acc, currKey) => {
      if (!isEmpty(fileLinks?.[currKey].files)) {
        return { ...acc, [currKey]: this.fileLinks[currKey] }
      }
      if (fileLinks && !isEmpty(fileLinks?.[currKey]?.reference?.file)) {
        return { ...acc, [currKey]: { ...this.fileLinks[currKey], files: [this.fileLinks[currKey]?.reference?.file] } }
      }
      return acc
    }, {})
    return files
  }

  get isFilesExist() {
    return !isEmpty(this.fileList)
  }

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  get project() {
    return this.$store.state.project.details
  }

  onESignCarouselChange(idx: number) {
    this.eSignCarouselIndex = idx
  }

  onShowAdjustment(cfKey: string, item: ILabelDisplay) {
    this.isShowAdjustmentField = true
    this.adjustmentField = {
      cfKey,
      label: item.label,
      value: item.value,
    }
  }

  onPreview(url: string) {
    window.open(url, '_blank')
  }

  onDisplayActivateImage() {
    if (this.isMaskedImage) return
    this.imageDialogVisible = true
  }

  onDisplaySignature() {
    if (this.isMaskedSignature) return
    this.signatureDialogVisible = true
  }

  onClickEdit() {
    this.$router.push({ name: 'edit', params: { view: this.view } })
  }

  onClickRefreshReferenceField() {
    this.$emit('update:onRefreshReferenceFieldClick')
  }

  showListStringModal(data: IListStringItem[], fieldLabel: string) {
    if (Array.isArray(data)) {
      this.listStringData = data.map((item) => {
        if (typeof item === 'string') {
          return {
            code: item,
            limitAmount: undefined,
            amount: undefined,
            unit: undefined,
            scannedDateTime: undefined,
          }
        }
        return item
      })
    } else {
      this.listStringData = []
    }
    this.currentFieldLabel = fieldLabel
    this.listStringModalVisible = true
  }

  closeListStringModal() {
    this.listStringModalVisible = false
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

::v-deep .list-string-modal {
  .el-dialog__header {
    padding-bottom: 0px;
  }
}

.masked-value {
  color: #ff0000;
}

.address-box {
  .label-box {
    margin-bottom: 12px;
    display: inline-block;
  }
}

.el-button.edit-button {
  padding: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
}

.tracking-point {
  background: $light-grey;
  padding: 4px 0;
  width: 100%;
  border: 0;
}

.label-and-value > .value {
  word-break: break-word;
}

.label-and-value > .label {
  word-break: break-word;
}

.image-dialog {
  &.el-dialog {
    .el-dialog__body {
      padding-top: 0;
    }
  }
}

.image-dialog:deep() {
  &.el-dialog {
    background-color: #f2f2f2;
    border-radius: 10px;
    max-height: 90%;
    overflow: hidden;
    flex-direction: column;
    display: flex;
    margin: 0;

    &__header {
      font-weight: bold;
      border-bottom: 1px solid #bdbdbd;
      padding: 25px;
    }

    .el-carousel {
      &__item {
        justify-content: center;
        display: flex;
      }
    }

    .el-image {
      overflow: auto;

      &__inner {
        height: auto;
      }
    }
  }
}

.file-and-value {
  display: flex;
  padding: 8px 0px;

  > div {
    flex-basis: 35%;
    text-align: right;
    margin-right: 8px;
    min-width: 35%;
  }
}

.adjustment-link {
  color: blue;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
}

.key-and-value {
  display: flex;
  margin: 8px 0px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;

  .label {
    margin-right: 8px;
    font-weight: 600;
    white-space: nowrap;
  }

  .value {
    width: 100%;
  }
}
</style>
